import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2, Search, Filter, Building2, FileQuestion, FileText, CheckCircle2, XCircle, Download, Upload, Tag } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Question categories for interview diversity
const QUESTION_CATEGORIES = [
  "Motivation & Reasons",
  "Education Quality",
  "Personal & Cultural Expectations",
  "Fixed Question 2",
  "UK Study Choice",
  "Campus Choice",
  "Course Understanding",
  "University Choice",
  "Sector Consideration",
  "Accommodation",
  "Funding",
  "Academic History",
  "Work Plans",
  "Post-Study Plans",
  "Career Goals",
  "Visa Refusal",
  "Dependants"
];

export default function AdminQuestions() {
  const { token } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterUniversity, setFilterUniversity] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    question_text: '',
    category: '',
    keywords: '',
    expected_points: '',
    answer_relevancy_guidance: '',
    weight: '1',
    listening_time: '30',
    answering_time: '90',
    university_id: '',
    status: 'active'
  });
  
  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Filter states
  const [filterCategory, setFilterCategory] = useState('all');
  
  // CSV Import states
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const csvFileRef = useRef(null);
  
  // Organization templates tab states
  const [activeTab, setActiveTab] = useState('generic');
  const [orgTemplates, setOrgTemplates] = useState([]);
  const [orgsWithQuestions, setOrgsWithQuestions] = useState([]);
  const [orgTemplatesLoading, setOrgTemplatesLoading] = useState(false);
  const [filterOrganization, setFilterOrganization] = useState('all');
  const [orgSearch, setOrgSearch] = useState('');

  useEffect(() => {
    fetchQuestions();
    fetchUniversities();
  }, [filterUniversity]);
  
  useEffect(() => {
    if (activeTab === 'organization') {
      fetchOrgTemplates();
      fetchOrgsWithQuestions();
    }
  }, [activeTab, filterOrganization]);

  const fetchQuestions = async () => {
    try {
      const params = {};
      if (filterUniversity === 'generic') {
        params.generic_only = true;
      } else if (filterUniversity !== 'all') {
        params.university_id = filterUniversity;
      }
      
      const response = await axios.get(`${API_URL}/api/questions`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setQuestions(response.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/universities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUniversities(response.data);
    } catch (error) {
      console.error('Failed to fetch universities');
    }
  };
  
  const fetchOrgTemplates = async () => {
    try {
      setOrgTemplatesLoading(true);
      const params = {};
      if (filterOrganization !== 'all') {
        params.organization_id = filterOrganization;
      }
      const response = await axios.get(`${API_URL}/api/admin/organization-questions`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setOrgTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch organization templates:', error);
      toast.error('Failed to load organization templates');
    } finally {
      setOrgTemplatesLoading(false);
    }
  };
  
  const fetchOrgsWithQuestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/organizations-with-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgsWithQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations with questions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      title: formData.title || null,
      question_text: formData.question_text,
      category: formData.category || null,
      keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
      expected_points: formData.expected_points.split('\n').map(p => p.trim()).filter(p => p),
      answer_relevancy_guidance: formData.answer_relevancy_guidance || null,
      weight: parseInt(formData.weight),
      listening_time: parseInt(formData.listening_time),
      answering_time: parseInt(formData.answering_time),
      university_id: formData.university_id || null,
      status: formData.status
    };

    try {
      if (editingQuestion) {
        await axios.put(
          `${API_URL}/api/questions/${editingQuestion.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Question updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/questions`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Question created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to save question');
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      title: question.title || '',
      question_text: question.question_text,
      category: question.category || '',
      keywords: question.keywords.join(', '),
      expected_points: question.expected_points.join('\n'),
      answer_relevancy_guidance: question.answer_relevancy_guidance || '',
      weight: question.weight.toString(),
      listening_time: question.listening_time.toString(),
      answering_time: question.answering_time.toString(),
      university_id: question.university_id || '',
      status: question.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/questions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const resetForm = () => {
    setEditingQuestion(null);
    setFormData({
      title: '',
      question_text: '',
      category: '',
      keywords: '',
      expected_points: '',
      answer_relevancy_guidance: '',
      weight: '1',
      listening_time: '30',
      answering_time: '90',
      university_id: '',
      status: 'active'
    });
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredQuestions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredQuestions.map(q => q.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API_URL}/api/admin/questions/bulk-delete`, 
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedIds.length} question(s) deleted`);
      setDeleteDialogOpen(false);
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete questions');
    } finally {
      setDeleting(false);
    }
  };

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.question_text.toLowerCase().includes(search.toLowerCase()) ||
      (q.title && q.title.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || 
      (filterCategory === 'uncategorized' ? !q.category : q.category === filterCategory);
    return matchesSearch && matchesCategory;
  });
  
  const filteredOrgTemplates = orgTemplates.filter(q =>
    q.question_text?.toLowerCase().includes(orgSearch.toLowerCase()) ||
    (q.title && q.title.toLowerCase().includes(orgSearch.toLowerCase())) ||
    (q.organization_name && q.organization_name.toLowerCase().includes(orgSearch.toLowerCase()))
  );

  const getUniversityName = (id) => {
    if (!id) return 'Generic';
    const uni = universities.find(u => u.id === id);
    return uni ? uni.name : 'Unknown';
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterUniversity === 'generic') {
        params.append('generic_only', 'true');
      } else if (filterUniversity && filterUniversity !== 'all') {
        params.append('university_id', filterUniversity);
      }
      
      const response = await axios.get(`${API_URL}/api/questions/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported ${response.data.total} questions`);
    } catch (error) {
      toast.error('Failed to export questions');
    } finally {
      setExporting(false);
    }
  };

  // Export as CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterUniversity === 'generic') {
        params.append('generic_only', 'true');
      } else if (filterUniversity && filterUniversity !== 'all') {
        params.append('university_id', filterUniversity);
      }
      
      const response = await axios.get(`${API_URL}/api/questions/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const questionsData = response.data.questions || [];
      
      // Create CSV headers
      const headers = ['Question', 'Category', 'Answer Relevancy', 'Keywords', 'Expected Points', 'Weight', 'Listening Time', 'Answering Time', 'Status', 'Type', 'University'];
      
      // Create CSV rows
      const rows = questionsData.map(q => [
        `"${(q.question_text || '').replace(/"/g, '""')}"`,
        `"${(q.category || '').replace(/"/g, '""')}"`,
        `"${(q.answer_relevancy_guidance || '').replace(/"/g, '""')}"`,
        `"${(q.keywords || []).join(', ').replace(/"/g, '""')}"`,
        `"${(q.expected_points || []).join(', ').replace(/"/g, '""')}"`,
        q.weight || 1,
        q.listening_time || 30,
        q.answering_time || 90,
        q.status || 'active',
        q.is_generic ? 'Generic' : 'University',
        q.university_id ? getUniversityName(q.university_id) : ''
      ]);
      
      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported ${questionsData.length} questions as CSV`);
    } catch (error) {
      toast.error('Failed to export questions');
    } finally {
      setExporting(false);
    }
  };

  // CSV Import handler
  const handleCSVFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      return;
    }
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) {
          toast.error('CSV must have a header row and at least one data row');
          return;
        }
        // Parse CSV properly (handle quoted fields)
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
                current += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };
        
        const headers = parseCSVLine(lines[0]);
        const rows = lines.slice(1).map(line => {
          const values = parseCSVLine(line);
          const obj = {};
          headers.forEach((h, i) => {
            obj[h.trim()] = values[i] || '';
          });
          return obj;
        }).filter(row => {
          // Filter out empty rows
          const questionText = row['Question'] || row['question_text'] || row['Question Text'] || '';
          return questionText.trim().length > 0;
        });
        
        setCsvPreview(rows);
        setCsvImportOpen(true);
        toast.success(`Parsed ${rows.length} questions from CSV`);
      } catch (err) {
        toast.error('Failed to parse CSV file');
        console.error('CSV parse error:', err);
      }
    };
    reader.readAsText(file);
    // Reset input so same file can be re-selected
    if (csvFileRef.current) csvFileRef.current.value = '';
  };

  const handleCSVImport = async () => {
    if (csvPreview.length === 0) return;
    setCsvImporting(true);
    try {
      // Map CSV columns to backend expected format
      const mappedQuestions = csvPreview.map(row => ({
        question_text: row['Question'] || row['question_text'] || row['Question Text'] || '',
        category: row['Category'] || row['category'] || '',
        answer_relevancy_guidance: row['Answer Relevancy'] || row['answer_relevancy_guidance'] || row['Answer Relevancy Guidance'] || '',
        keywords: row['Keywords'] || row['keywords'] || '',
        expected_points: row['Expected Points'] || row['expected_points'] || '',
        weight: row['Weight'] || row['weight'] || '1',
        listening_time: row['Listening Time'] || row['listening_time'] || '30',
        answering_time: row['Answering Time'] || row['answering_time'] || '90',
        university_id: row['University ID'] || row['university_id'] || '',
        organization_id: row['Organization ID'] || row['organization_id'] || '',
        status: row['Status'] || row['status'] || 'active'
      }));
      
      const response = await axios.post(`${API_URL}/api/admin/questions/bulk-import`, 
        { questions: mappedQuestions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Imported ${response.data.imported} questions (${response.data.skipped} skipped)`);
        if (response.data.errors?.length > 0) {
          response.data.errors.forEach(err => toast.warning(err));
        }
        setCsvImportOpen(false);
        setCsvPreview([]);
        setCsvFileName('');
        fetchQuestions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import questions');
    } finally {
      setCsvImporting(false);
    }
  };

  const downloadCSVTemplate = () => {
    const headers = ['Question', 'Category', 'Answer Relevancy', 'Keywords', 'Expected Points', 'Weight', 'Listening Time', 'Answering Time', 'Status'];
    const sampleRow = ['"Why did you choose this university?"', '"University Choice"', '"Student should mention specific programs, rankings, or campus features"', '"university,choice,programs"', '"mentions specific program,discusses rankings"', '1', '30', '90', 'active'];
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_import_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('Template downloaded');
  };

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl">Question Bank</h2>
            <p className="text-muted-foreground">Manage interview questions</p>
          </div>
          <div className="flex gap-2">
            <input 
              type="file" 
              ref={csvFileRef} 
              accept=".csv" 
              onChange={handleCSVFileSelect} 
              className="hidden" 
              data-testid="csv-file-input"
            />
            <Button 
              variant="outline" 
              onClick={() => csvFileRef.current?.click()}
              data-testid="import-csv-questions"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting} data-testid="export-questions">
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExport}>
                  <FileQuestion className="w-4 h-4 mr-2" />
                  Export as JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCSV}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {selectedIds.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="bulk-delete-questions"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected ({selectedIds.length})
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-lime-500 hover:bg-lime-600 text-white" data-testid="add-question">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title (Optional)</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Motivation Question"
                        data-testid="question-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select
                        value={formData.category || "none"}
                        onValueChange={(value) => setFormData({ ...formData, category: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="question-category">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select a category</SelectItem>
                          {QUESTION_CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="university_id">University</Label>
                      <Select
                        value={formData.university_id || "generic"}
                        onValueChange={(value) => setFormData({ ...formData, university_id: value === "generic" ? "" : value })}
                      >
                        <SelectTrigger data-testid="question-university">
                          <SelectValue placeholder="Generic (All Universities)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="generic">Generic (All Universities)</SelectItem>
                          {universities.map(uni => (
                            <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger data-testid="question-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="question_text">Question Text *</Label>
                    <Textarea
                      id="question_text"
                      value={formData.question_text}
                      onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                      rows={3}
                      required
                      placeholder="Enter the interview question"
                      data-testid="question-text"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                    <Input
                      id="keywords"
                      value={formData.keywords}
                      onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                      placeholder="motivation, career, growth, education"
                      data-testid="question-keywords"
                    />
                    <p className="text-xs text-muted-foreground">Keywords used for AI scoring</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expected_points">Expected Answer Points (one per line)</Label>
                    <Textarea
                      id="expected_points"
                      value={formData.expected_points}
                      onChange={(e) => setFormData({ ...formData, expected_points: e.target.value })}
                      rows={3}
                      placeholder="Clear career goals&#10;Interest in the program&#10;Research about the university"
                      data-testid="question-points"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="answer_relevancy_guidance">Answer Relevancy / Expected Answer Guidance</Label>
                    <Textarea
                      id="answer_relevancy_guidance"
                      value={formData.answer_relevancy_guidance || ''}
                      onChange={(e) => setFormData({ ...formData, answer_relevancy_guidance: e.target.value })}
                      rows={4}
                      placeholder="Describe what the student's answer should cover. E.g.: The student should explain specific reasons for selecting the university, such as course content, modules, ranking, facilities, location, or career alignment. The answer should demonstrate research rather than generic reasons."
                      data-testid="question-answer-guidance"
                    />
                    <p className="text-xs text-muted-foreground">AI uses this guidance alongside keywords to evaluate answer relevancy and context</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="weight">Weight</Label>
                      <Input
                        id="weight"
                        type="number"
                        min="1"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        data-testid="question-weight"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="listening_time">Read Time (s)</Label>
                      <Input
                        id="listening_time"
                        type="number"
                        value={formData.listening_time}
                        onChange={(e) => setFormData({ ...formData, listening_time: e.target.value })}
                        data-testid="question-listening"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="answering_time">Answer Time (s)</Label>
                      <Input
                        id="answering_time"
                        type="number"
                        value={formData.answering_time}
                        onChange={(e) => setFormData({ ...formData, answering_time: e.target.value })}
                        data-testid="question-answering"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-white" data-testid="save-question">
                      {editingQuestion ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="generic" className="flex items-center gap-2">
              <FileQuestion className="w-4 h-4" />
              Generic Questions
            </TabsTrigger>
            <TabsTrigger value="organization" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Organization Templates
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="generic" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <Select value={filterUniversity} onValueChange={setFilterUniversity}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filter by university" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Questions</SelectItem>
                        <SelectItem value="generic">Generic Only</SelectItem>
                        {universities.map(uni => (
                          <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-52">
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="uncategorized">Uncategorized</SelectItem>
                        {QUESTION_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search questions..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 w-64"
                      data-testid="search-questions"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : filteredQuestions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No questions found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={selectedIds.length === filteredQuestions.length && filteredQuestions.length > 0}
                              onCheckedChange={toggleSelectAll}
                              data-testid="select-all-questions"
                            />
                          </TableHead>
                          <TableHead className="w-[35%]">Question</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>University</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredQuestions.map((q) => (
                          <TableRow key={q.id} data-testid={`question-row-${q.id}`}>
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.includes(q.id)}
                                onCheckedChange={() => toggleSelect(q.id)}
                                data-testid={`select-question-${q.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                {q.title && <p className="font-medium text-sm">{q.title}</p>}
                                <p className="text-sm text-muted-foreground line-clamp-2">{q.question_text}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {q.category ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  <Tag className="w-3 h-3 mr-1" />
                                  {q.category}
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={q.university_id ? 'default' : 'secondary'}>
                                {getUniversityName(q.university_id)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {q.listening_time}s / {q.answering_time}s
                            </TableCell>
                            <TableCell>
                              <Badge variant={q.status === 'active' ? 'default' : 'secondary'}>
                                {q.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="icon" onClick={() => handleEdit(q)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(q.id)} className="text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="organization" className="mt-6 space-y-6">
            {/* Organizations Summary */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {orgsWithQuestions.map((org) => (
                <Card key={org.id} className={`cursor-pointer transition-all ${filterOrganization === org.id ? 'border-lime-500 ring-1 ring-lime-500' : 'hover:border-lime-500/50'}`}
                      onClick={() => setFilterOrganization(filterOrganization === org.id ? 'all' : org.id)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{org.name}</p>
                        <p className="text-sm text-muted-foreground">{org.question_count} questions</p>
                      </div>
                      {org.use_own_questions ? (
                        <Badge className="bg-lime-500/10 text-lime-600">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {orgsWithQuestions.length === 0 && (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No organizations have created their own question banks yet.</p>
                </div>
              )}
            </div>
            
            {/* Organization Questions Table */}
            {orgsWithQuestions.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row gap-4 justify-between">
                    <CardTitle className="font-heading">
                      {filterOrganization === 'all' 
                        ? 'All Organization Questions' 
                        : `Questions from ${orgsWithQuestions.find(o => o.id === filterOrganization)?.name || 'Organization'}`}
                    </CardTitle>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search organization questions..."
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {orgTemplatesLoading ? (
                    <div className="text-center py-8">Loading...</div>
                  ) : filteredOrgTemplates.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">No questions found</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[35%]">Question</TableHead>
                            <TableHead>Organization</TableHead>
                            <TableHead>Keywords</TableHead>
                            <TableHead>Time</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredOrgTemplates.map((q) => (
                            <TableRow key={q.id}>
                              <TableCell>
                                <div>
                                  {q.title && <p className="font-medium text-sm">{q.title}</p>}
                                  <p className="text-sm text-muted-foreground line-clamp-2">{q.question_text}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-lime-500/10 text-lime-700">
                                  <Building2 className="w-3 h-3 mr-1" />
                                  {q.organization_name}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1 max-w-[150px]">
                                  {q.keywords?.slice(0, 3).map((kw, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                                  ))}
                                  {q.keywords?.length > 3 && (
                                    <Badge variant="outline" className="text-xs">+{q.keywords.length - 3}</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">
                                {q.listening_time}s / {q.answering_time}s
                              </TableCell>
                              <TableCell>
                                <Badge variant={q.status === 'active' ? 'default' : 'secondary'}>
                                  {q.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to delete {selectedIds.length} question(s)? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete}
              disabled={deleting}
              data-testid="confirm-bulk-delete-questions"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Preview Dialog */}
      <Dialog open={csvImportOpen} onOpenChange={(open) => { setCsvImportOpen(open); if (!open) { setCsvPreview([]); setCsvFileName(''); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Questions from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {csvFileName && <><strong>{csvFileName}</strong> — </>}
                {csvPreview.length} question(s) found
              </p>
              <Button variant="ghost" size="sm" onClick={downloadCSVTemplate} data-testid="download-csv-template">
                <Download className="w-4 h-4 mr-1" />
                Download Template
              </Button>
            </div>
            {csvPreview.length > 0 && (
              <div className="border rounded-lg overflow-auto max-h-[400px]">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium">#</th>
                      <th className="p-2 text-left font-medium">Question</th>
                      <th className="p-2 text-left font-medium">Category</th>
                      <th className="p-2 text-left font-medium">Answer Relevancy</th>
                      <th className="p-2 text-left font-medium">Keywords</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.slice(0, 50).map((row, idx) => (
                      <tr key={idx} className="border-t hover:bg-muted/50">
                        <td className="p-2 text-muted-foreground">{idx + 1}</td>
                        <td className="p-2 max-w-[300px] truncate">{row['Question'] || row['question_text'] || row['Question Text'] || '—'}</td>
                        <td className="p-2">{row['Category'] || row['category'] || '—'}</td>
                        <td className="p-2 max-w-[200px] truncate">{row['Answer Relevancy'] || row['answer_relevancy_guidance'] || row['Answer Relevancy Guidance'] || '—'}</td>
                        <td className="p-2 max-w-[150px] truncate">{row['Keywords'] || row['keywords'] || '—'}</td>
                      </tr>
                    ))}
                    {csvPreview.length > 50 && (
                      <tr><td colSpan={5} className="p-2 text-center text-muted-foreground">... and {csvPreview.length - 50} more</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCsvImportOpen(false); setCsvPreview([]); setCsvFileName(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCSVImport}
              disabled={csvImporting || csvPreview.length === 0}
              className="bg-lime-500 hover:bg-lime-600 text-white"
              data-testid="confirm-csv-import"
            >
              {csvImporting ? 'Importing...' : `Import ${csvPreview.length} Questions`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
